import React from 'react';

import { Box, Paper, Stack, Typography } from '@mui/material';

import { Nullable } from '@clearverify/types';

import {
  Table,
  TableColumn,
  TableIsRowSelectableFn,
  TableRowAction,
  TableSearch,
  TableSelectedRowId,
  TableThemes,
  TableTopAction,
} from 'src/components/table';
import { DefaultDataRow } from 'src/components/table/types';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import { SxProp } from 'src/types/mui';

export type HiddenResultsTableProps<TRecord extends DefaultDataRow> = {
  rowActions: Array<TableRowAction<TRecord>>;
  topActions?: Array<TableTopAction>;
  columns: Array<TableColumn<TRecord>>;
  hideTableTopMenu?: boolean;
  selectable?: boolean;
  withPaper?: boolean;
  tableSearchSx?: SxProp;
  pagination: PaginationContextValue;
  isRowSelectable?: TableIsRowSelectableFn<TRecord>;
  selectedProps?: {
    selectedIds: TableSelectedRowId[];
    setSelectedIds: React.Dispatch<React.SetStateAction<TableSelectedRowId[]>>;
  };
  items: Array<TRecord>;
  itemsCount: number;
  loading: boolean;
  description?: string;
  searchPlaceholder?: string;
  isRightMenu?: boolean;
  onRefresh?: () => Promise<void>;
  searchFieldSize?: 'small' | 'medium';
};

export const HiddenResultsTable = <T extends { id?: Nullable<string> }>({
  rowActions,
  topActions,
  columns,
  items,
  itemsCount,
  pagination,
  selectable = false,
  hideTableTopMenu = true,
  tableSearchSx,
  isRowSelectable,
  withPaper = false,
  selectedProps,
  loading,
  description = 'Employees data is hidden. Type in SSN number or Employee ID to find particular requests',
  searchPlaceholder = 'SSN or Employee ID',
  isRightMenu = false,
  onRefresh,
  searchFieldSize = 'medium',
}: HiddenResultsTableProps<T>) => {
  const { setSearchValue, searchValue } = useSearch();
  const { setSortOption } = useFilterStorage();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const isFooterHidden = itemsCount <= (pagination.rowsPerPageOptions[0] || pagination.rowsPerPage);

  const hasSearchValue = searchValue && searchValue.length > 0;

  const searchFieldPositionAbsolute =
    isRightMenu && hasSearchValue && !hideTableTopMenu && topActions?.length;

  const table = (
    <Stack gap="20px" sx={stackStyles}>
      <Typography color="text.secondary">{description}</Typography>
      <Box
        sx={{
          ...tableSearchSx,
          position: searchFieldPositionAbsolute ? 'absolute' : 'initial',
          top: 42,
          zIndex: 10,
        }}
      >
        <TableSearch
          size={searchFieldSize}
          onSearch={setSearchValue}
          placeholder={searchPlaceholder}
        />
      </Box>
      {hasSearchValue ? (
        <Table
          loading={loading}
          dataSource={items}
          rowCount={itemsCount}
          columns={columns}
          rowActions={rowActions}
          topActions={topActions}
          hideTableTopMenu={hideTableTopMenu}
          selectedIds={selectedProps?.selectedIds}
          setSelectedIds={selectedProps?.setSelectedIds}
          isRowSelectable={isRowSelectable}
          pagination={pagination}
          selectable={selectable}
          hideFooter={isFooterHidden}
          isRightMenu={isRightMenu}
          tableTheme={TableThemes.hiddenResult}
          onRefresh={onRefresh}
          onSort={setSortOption}
        />
      ) : null}
    </Stack>
  );

  if (withPaper) {
    return (
      <Paper variant="outlined" sx={paperStyles}>
        {table}
      </Paper>
    );
  }

  return table;
};

const stackStyles: SxProp = { mt: 1, position: 'relative', padding: '0 16px 16px' };

const paperStyles: SxProp = {
  padding: '16px',
  borderRadius: 2,
};
